import React, { useState, useEffect, useMemo } from 'react';
import LinkButton from './LinkButton';

const ALL_RECIPIENTS = 'All';

// Deep link to a location, matching the address-bar format the app reads on
// load: /<env>/<questId>?location=<uuid>. The tags summary always reads the
// test environment, so the slug is always `test`. Lets middle/ctrl-click open
// the location in a new tab.
const locationHref = (item) => {
  const path = `/test/${encodeURIComponent(item.quest_id)}`;
  return item.location_uuid
    ? `${path}?location=${encodeURIComponent(item.location_uuid)}`
    : path;
};

const TagsSummary = ({ onBack, backHref, onNavigateToLocation, currentQuestId, currentUsername }) => {
  const [tagsSummary, setTagsSummary] = useState([]);
  const [commentsSummary, setCommentsSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState(null); // null | '__me__' | username
  const [expandedComments, setExpandedComments] = useState({});

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        setLoading(true);
        const [tagsResponse, commentsResponse] = await Promise.all([
          fetch('/api/quest-tags-summary', { credentials: 'include' }),
          fetch('/api/quest-comments-summary', { credentials: 'include' })
        ]);

        if (!tagsResponse.ok) {
          throw new Error('Failed to fetch tags summary');
        }
        if (!commentsResponse.ok) {
            throw new Error('Failed to fetch comments summary');
        }

        const tagsData = await tagsResponse.json();
        const commentsData = await commentsResponse.json();
        
        setTagsSummary(tagsData);
        setCommentsSummary(commentsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSummaries();
  }, []);

  const uniqueTags = useMemo(() => {
    const allTags = new Set();
    tagsSummary.forEach(item => {
      item.tags.forEach(tag => allTags.add(tag));
    });
    return Array.from(allTags).sort();
  }, [tagsSummary]);

  const handleToggleFilterTag = (tag) => {
    setSelectedFilterTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  // Everyone who is a recipient of at least one tag (excluding the "All" pseudo).
  const uniqueAssignees = useMemo(() => {
    const s = new Set();
    tagsSummary.forEach(item => {
      Object.values(item.tagAssignees || {}).forEach(list => {
        (list || []).forEach(u => { if (u !== ALL_RECIPIENTS) s.add(u); });
      });
    });
    return Array.from(s).sort();
  }, [tagsSummary]);

  const commentsMap = useMemo(() => {
    const map = new Map();
    commentsSummary.forEach(quest => {
      quest.locations_with_comments?.forEach(location => {
        map.set(`${quest.quest_id}-${location.id}`, location.comment);
      });
    });
    return map;
  }, [commentsSummary]);

  const filteredSummary = useMemo(() => {
    const target = assigneeFilter === '__me__' ? currentUsername : assigneeFilter;
    return tagsSummary.filter(item => {
      // Tag filter: OR logic — at least one of the selected tags is present.
      const tagOk = selectedFilterTags.length === 0
        || selectedFilterTags.some(filterTag => item.tags.includes(filterTag));
      if (!tagOk) return false;
      // Assignee filter: at least one tag is assigned to the target (or to All).
      if (!assigneeFilter) return true;
      if (!target) return false;
      return (item.tags || []).some(tag => {
        const list = (item.tagAssignees && item.tagAssignees[tag]) || [];
        return list.includes(target) || list.includes(ALL_RECIPIENTS);
      });
    });
  }, [tagsSummary, selectedFilterTags, assigneeFilter, currentUsername]);

  const summaryWithComments = useMemo(() => {
    return filteredSummary.map(item => ({
      ...item,
      comment: commentsMap.get(`${item.quest_id}-${item.location_id}`) || null,
    }));
  }, [filteredSummary, commentsMap]);

  const questsGrouped = useMemo(() => {
    const grouped = {};
    summaryWithComments.forEach(item => {
      const questKey = `${item.quest_id}|${item.quest_name}`;
      if (!grouped[questKey]) {
        grouped[questKey] = {
          quest_name: item.quest_name,
          quest_id: item.quest_id,
          locations: [],
        };
      }
      grouped[questKey].locations.push(item);
    });
    const arr = Object.values(grouped);
    // Float the currently-open quest to the top; the rest keep their order
    // (Array.prototype.sort is stable).
    if (currentQuestId) {
      arr.sort((a, b) => {
        if (a.quest_id === currentQuestId) return -1;
        if (b.quest_id === currentQuestId) return 1;
        return 0;
      });
    }
    return arr;
  }, [summaryWithComments, currentQuestId]);

  const handleToggleComment = (questId, locationId) => {
    const key = `${questId}-${locationId}`;
    setExpandedComments(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return <div className="loading">Loading tags summary...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const getTagStyle = (tag) => {
    const lowerCaseTag = tag.toLowerCase();
    if (lowerCaseTag === 'achtung') {
      return { backgroundColor: '#e74c3c', color: 'white' };
    }
    if (lowerCaseTag === 'good') {
      return { backgroundColor: '#2ecc71', color: 'white' };
    }
    if (lowerCaseTag === 'acceptable') {
      return { backgroundColor: '#f39c12', color: 'white' };
    }
    // For custom tags, use the default style defined in CSS for .tag-chip-summary
    return {};
  };

  return (
    <div className="audio-stats-container"> {/* Reusing styles */}
      <div className="audio-stats-header">
        <h1>Tags Summary</h1>
        <LinkButton href={backHref} onClick={onBack} className="back-button">Back to Editor</LinkButton>
      </div>

      <div className="tags-filter-container">
        <span className="tags-filter-label">Filter by tags (OR logic):</span>
        <div className="tags-filter-buttons">
          {uniqueTags.map(tag => (
            <button
              key={tag}
              className={`tag-filter-button ${selectedFilterTags.includes(tag) ? 'active' : ''}`}
              onClick={() => handleToggleFilterTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
        {selectedFilterTags.length > 0 && (
          <button onClick={() => setSelectedFilterTags([])} className="tag-filter-clear">
            Clear Filters
          </button>
        )}
      </div>

      <div className="tags-filter-container">
        <span className="tags-filter-label">Assigned to:</span>
        <div className="tags-filter-buttons">
          <button
            className={`tag-filter-button ${assigneeFilter === null ? 'active' : ''}`}
            onClick={() => setAssigneeFilter(null)}
          >
            All
          </button>
          {currentUsername && (
            <button
              className={`tag-filter-button ${assigneeFilter === '__me__' ? 'active' : ''}`}
              onClick={() => setAssigneeFilter('__me__')}
            >
              Assigned to me
            </button>
          )}
          {uniqueAssignees.map(u => (
            <button
              key={u}
              className={`tag-filter-button ${assigneeFilter === u ? 'active' : ''}`}
              onClick={() => setAssigneeFilter(u)}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      <div className="table-responsive">
        <table className="audio-stats-table">
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Quest</th>
              <th style={{ width: '20%' }}>Location</th>
              <th style={{ width: '20%' }}>Tags</th>
              <th style={{ width: '30%' }}>Comment</th>
            </tr>
          </thead>
          <tbody>
            {questsGrouped.map((quest) => (
              <React.Fragment key={quest.quest_id}>
                {quest.locations.map((item, index) => (
                  <tr key={`${item.quest_id}-${item.location_id}`}>
                    {index === 0 && (
                      <td
                        rowSpan={quest.locations.length}
                        data-label="Quest"
                        className={quest.quest_id === currentQuestId ? 'current-quest-cell' : ''}
                      >
                        {quest.quest_name}
                        {quest.quest_id === currentQuestId && <span className="current-quest-badge">current</span>}
                      </td>
                    )}
                    <td data-label="Location">
                      <a
                        className="location-link"
                        href={locationHref(item)}
                        onClick={(e) => {
                          // Let the browser handle new-tab/window clicks natively
                          // (middle-click fires no onClick at all; ctrl/cmd/shift
                          // are caught here). A plain left click navigates in-app.
                          if (
                            e.button !== 0 ||
                            e.metaKey ||
                            e.ctrlKey ||
                            e.shiftKey ||
                            e.altKey
                          ) {
                            return;
                          }
                          e.preventDefault();
                          onNavigateToLocation(item.quest_id, item.location_id);
                        }}
                      >
                        {item.location_order}. {item.location_name}
                      </a>
                    </td>
                    <td data-label="Tags">
                      <div className="tags-list-summary">
                        {(item.tags || []).map(tag => {
                          const asg = (item.tagAssignees && item.tagAssignees[tag]) || [];
                          return (
                            <span key={tag} className="tag-chip-summary" style={getTagStyle(tag)}>
                              {tag}
                              {asg.length > 0 && (
                                <span className="tag-assignee-summary"> → {asg.join(', ')}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td data-label="Comment">
                      {item.comment ? (
                        <div
                          onClick={() => handleToggleComment(item.quest_id, item.location_id)}
                          className={`comment-cell-wrapper ${expandedComments[`${item.quest_id}-${item.location_id}`] ? 'expanded' : ''}`}
                        >
                          {item.comment}
                        </div>
                      ) : (
                        <span className="no-missing">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {questsGrouped.length === 0 && (
                <tr>
                    <td colSpan="4" style={{ textAlign: 'center' }}>
                      {tagsSummary.length > 0 ? 'No locations match the selected filters.' : 'No tags or comments found.'}
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="scroll-spacer" />
    </div>
  );
};

export default TagsSummary; 